import React, { useMemo, useState } from "react"
import { Information } from "../../simulation/types"
import { TutorialConfig, TutorialStep } from "../types/tutorial-config"
import { interpolateVariables } from "../utils/step-builder"
import {
  buildTreeNodeSelectorMap,
  resolveTreeNodeId,
} from "../utils/tree-resolver"

type Props = {
  config: TutorialConfig
  categories: Information[]
  testId: number
}

const PREVIEW_STORAGE_KEY = "__tutorialBuilderPreview"

const LivePreview: React.FC<Props> = ({ config, categories, testId }) => {
  const [stepIndex, setStepIndex] = useState(0)
  const step: TutorialStep | undefined = config.steps[stepIndex]

  const interpolationVars = useMemo(() => {
    const selectorMap = buildTreeNodeSelectorMap(categories, config.treeNodeMappings)
    return { ...(config.variables ?? {}), ...selectorMap }
  }, [categories, config.treeNodeMappings, config.variables])

  const renderText = (text: string | string[]): string => {
    const arr = Array.isArray(text) ? text : [text]
    return arr.map((t) => interpolateVariables(t, interpolationVars)).join("\n\n")
  }

  const resolvedAttach = useMemo(() => {
    if (!step?.attachTo) return null
    const interp = interpolateVariables(step.attachTo.element, interpolationVars)
    return interp
  }, [step, interpolationVars])

  const launchInSimulation = () => {
    try {
      window.localStorage.setItem(
        PREVIEW_STORAGE_KEY,
        JSON.stringify({ testId, config, stepIndex }),
      )
    } catch (e) {
      console.warn("Failed to save preview to localStorage", e)
    }
    const url = `/simulations/${testId}/preview?tutorialPreview=1`
    window.open(url, "_blank")
  }

  if (config.steps.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-500">
        No steps to preview yet. Add a step in the left rail.
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full text-sm">
      <div className="px-3 py-2 border-b font-semibold bg-gray-50 flex items-center gap-2">
        <span>Preview</span>
        <span className="text-xs text-gray-500 ml-auto">
          Step {stepIndex + 1} / {config.steps.length}
        </span>
      </div>
      <div className="px-3 py-2 border-b flex flex-wrap items-center gap-1">
        <button
          type="button"
          onClick={() => setStepIndex(Math.max(0, stepIndex - 1))}
          disabled={stepIndex === 0}
          className="text-xs px-2 py-1 border rounded disabled:opacity-50">
          ← Prev
        </button>
        <button
          type="button"
          onClick={() =>
            setStepIndex(Math.min(config.steps.length - 1, stepIndex + 1))
          }
          disabled={stepIndex === config.steps.length - 1}
          className="text-xs px-2 py-1 border rounded disabled:opacity-50">
          Next →
        </button>
        <select
          value={stepIndex}
          onChange={(e) => setStepIndex(Number(e.target.value))}
          className="text-xs border rounded px-1 py-1">
          {config.steps.map((s, i) => (
            <option key={s.id} value={i}>
              {i + 1}. {s.id}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setStepIndex(0)}
          className="text-xs px-2 py-1 border rounded ml-auto">
          ↺ Restart
        </button>
        <button
          type="button"
          onClick={launchInSimulation}
          title="Opens the simulation in a new tab with this in-progress config"
          className="text-xs px-2 py-1 bg-indigo-600 text-white rounded">
          Open in simulation
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {step && (
          <>
            <div className="text-xs uppercase tracking-wide text-gray-500">
              {step.type}
            </div>
            <div className="text-base font-medium">{step.id}</div>
            <div className="border rounded bg-white p-3 shadow-sm">
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: renderText(step.text) }}
              />
            </div>
            {step.attachTo && (
              <div className="text-xs">
                <span className="font-medium">Attaches to: </span>
                <code className="font-mono">{resolvedAttach}</code>
                <span className="text-gray-500"> ({step.attachTo.on})</span>
              </div>
            )}
            {step.advanceOn && (
              <div className="text-xs">
                <span className="font-medium">Advances when: </span>
                <code className="font-mono">{step.advanceOn.event}</code>
                <span className="text-gray-500"> on </span>
                <code className="font-mono">
                  {interpolateVariables(step.advanceOn.selector, interpolationVars)}
                </code>
              </div>
            )}
            {step.onShow && step.onShow.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer">
                  onShow ({step.onShow.length})
                </summary>
                <ul className="ml-4 list-disc">
                  {step.onShow.map((a, i) => (
                    <li key={i}>
                      <code>{a.action}</code> →{" "}
                      <code>{interpolateVariables(a.target, interpolationVars)}</code>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-500">
            Resolved tree mappings
          </summary>
          <ul className="ml-4 list-disc">
            {config.treeNodeMappings.map((m) => {
              const id = resolveTreeNodeId(categories, m.nodePath)
              return (
                <li key={m.name}>
                  <code>{m.name}</code> →{" "}
                  {id !== null ? (
                    <code>#tree-item-{id}</code>
                  ) : (
                    <span className="text-red-600">unresolved</span>
                  )}
                </li>
              )
            })}
            {config.treeNodeMappings.length === 0 && (
              <li className="text-gray-500 italic list-none">none</li>
            )}
          </ul>
        </details>
      </div>
    </div>
  )
}

export default LivePreview
export { PREVIEW_STORAGE_KEY }
